<template>
  <div class="min-h-screen px-6 md:px-12 py-16 md:py-24">
    <div class="max-w-6xl mx-auto">
      <!-- Journal Header -->
      <transition name="fade" appear>
        <div class="text-center mb-20" v-if="showHeader">
          <h1 class="text-4xl md:text-6xl font-bold mb-6 leading-tight">
            Timeline and <span class="text-accent">Accomplishments</span>
          </h1>

          <div class="max-w-2xl mx-auto">
            <p class="text-xl text-muted-foreground mb-8">About Rob Rickard</p>
          </div>

          <div
            class="w-32 h-1 bg-gradient-to-r from-accent to-accent/30 mx-auto"
          ></div>
        </div>
      </transition>
      <div class="flex flex-col gap-8 mb-12">
        <Motion
          as="section"
          :initial="{ opacity: 0, y: 30 }"
          :animate="{ opacity: 1, y: 0 }"
          :transition="{ duration: 0.6, delay: 0 * 0.4 }"
        >
          <div
            class="p-8 rounded-2xl border border-secondary/30 bg-gradient-to-br from-blue-400/10 to-cyan-200/10 shadow-lg hover:scale-105 transition-transform duration-300"
          >
            <h5 class="text-xl font-bold text-accent mb-4">
              Tech Entrepreneur, Thought Leader, and Product Strategist
            </h5>
            <p class="text-muted-foreground leading-relaxed">
              Rob Rickard is a veteran technology entrepreneur and product
              strategist with decades of experience building high-impact
              software systems and execution-driven teams. Based in Kigali,
              Rwanda, he is the Founder and Interim CEO of Guhemba Ltd., a
              fintech company designing Africa’s next generation of cashless
              payment infrastructure.<br />He also serves as Director of the
              Rwanda Build Program (RWBuild) — a Kigali-based tech hub and
              startup studio developing software talent and launching new
              African tech ventures. Since its founding, RWBuild has trained
              over 200 developers and contributed to the launch of multiple
              software products, becoming a trusted ecosystem partner to
              government institutions, universities, and global health
              organizations.
            </p>
          </div>
        </Motion>
        <Motion
          as="section"
          :initial="{ opacity: 0, y: 30 }"
          :animate="{ opacity: 1, y: 0 }"
          :transition="{ duration: 0.6, delay: 1 * 0.4 }"
        >
          <div
            class="p-8 rounded-2xl border border-secondary/30 bg-gradient-to-br from-purple-400/10 to-pink-200/10 shadow-lg hover:scale-105 transition-transform duration-300"
          >
            <h5 class="text-xl font-bold text-accent mb-4">
              Leadership & Legacy
            </h5>
            <p class="text-muted-foreground leading-relaxed">
              As Chair of Common World Inc. (CWInc), Rob brings more than twenty
              years of leadership from one of the early U.S. firms to bridge
              technology, culture, and organizational transformation. Under his
              direction, CWInc delivered strategic technology and communication
              projects for over 180 clients across education, small business,
              and the nonprofit sector. Throughout his career, Rob has been
              recognized for his ability to simplify complex systems, build
              execution-driven teams, and design frameworks that scale.<br />His
              work in communication and productivity has influenced
              organizational leaders and founders alike, and he has been a
              frequent speaker and mentor on how technology can accelerate
              impact and change.<br />Rob has been recognized by ministries,
              universities, organizations, global health partners, and many of
              his past clients. Rwanda Chamber of ICT listed him as top and
              leading mentor during his time with KLab.
            </p>
          </div>
        </Motion>
        <Motion
          as="section"
          :initial="{ opacity: 0, y: 30 }"
          :animate="{ opacity: 1, y: 0 }"
          :transition="{ duration: 0.6, delay: 2 * 0.4 }"
        >
          <div
            class="p-8 rounded-2xl border border-secondary/30 bg-gradient-to-br from-yellow-400/10 to-orange-200/10 shadow-lg hover:scale-105 transition-transform duration-300"
          >
            <h5 class="text-xl font-bold text-accent mb-4">Current Focus</h5>
            <p class="text-muted-foreground leading-relaxed">
              Rob’s current work centers on Guhemba and the expansion of
              RWBuild’s startup studio model. Guhemba aims to become the fintech
              operating system for Rwanda and greater Africa—supporting
              merchants, customers, and the broader vision for a cashless
              society.<br />At RWBuild, Rob leads a long-term mission to grow
              Rwanda into a leading African technology hub. RWBuild provides
              hands-on mentorship, design and development support, and startup
              acceleration pathways for founders across ideation, planning,
              product development, launch, and early scale.<br />Today, CWInc
              has evolved into a global platform for innovation, capacity
              building, and early-stage investment through RWBuild — a program
              advancing Rwanda’s developer ecosystem and startup pipeline.
              Across all his ventures, Rob continues to merge technical depth,
              human-centered design, and long-term strategy to create companies
              that not only solve problems but build capacity within the
              communities they serve. His mission remains clear: to design
              technology that empowers people, scales sustainably, and leaves
              lasting systems behind.
            </p>
          </div>
        </Motion>
      </div>
      <!-- Timeline -->
      <transition name="fade" appear>
        <div class="text-center mb-20" v-if="showHeader">
          <div
            class="inline-block p-1 bg-gradient-to-r from-accent/20 to-accent/5 rounded-lg mb-6"
          >
            <div class="bg-background px-6 py-3 rounded-md">
              <span
                class="text-accent font-medium tracking-wide uppercase text-sm"
                >Professional Journal</span
              >
            </div>
          </div>

          <h1 class="text-4xl md:text-6xl font-bold mb-6 leading-tight">
            Professional Timeline: <span class="text-accent">Rob Rickard</span>
          </h1>

          <div class="max-w-2xl mx-auto">
            <p class="text-xl text-muted-foreground mb-8">
              Tech Entrepreneur | Startup Builder | Innovation Leader in Rwanda
              &amp; the U.S.
            </p>
          </div>

          <div
            class="w-32 h-1 bg-gradient-to-r from-accent to-accent/30 mx-auto"
          ></div>
        </div>
      </transition>
      <div class="relative">
        <div
          class="absolute left-0 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-accent/50 via-accent/30 to-accent/10 transform md:-translate-x-px"
        ></div>

        <div class="space-y-16">
          <div
            v-for="(period, index) in timelinePeriods"
            :key="index"
            v-intersect.once="() => (revealItems[index] = true)"
            class="relative grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16"
            :class="{ 'md:grid-flow-col-dense': index % 2 !== 0 }"
          >
            <!-- Timeline Dot -->
            <div
              class="transition-transform duration-200 hover:scale-105 absolute left-0 md:left-1/2 w-4 h-4 bg-accent rounded-full border-4 border-background shadow-lg transform -translate-x-2 md:-translate-x-2 z-10 mt-6"
            ></div>

            <!-- Year Badge -->
            <transition name="scale-fade" appear>
              <div
                v-if="revealItems[index]"
                class="absolute left-8 md:left-1/2 transform md:-translate-x-1/2 z-20"
              >
                <div
                  class="bg-accent text-background px-4 py-2 rounded-full font-bold text-sm whitespace-nowrap shadow-lg"
                >
                  {{ period.years }}
                </div>
              </div>
            </transition>
            <!-- <MapPin class="w-5 h-5 text-accent" />  -->

            <!-- Content Card with AOS fade-left -->
            <div
              v-if="revealItems[index]"
              :class="[
                'mt-16 md:mt-0 ml-8 md:ml-0',
                index % 2 === 0 ? 'md:pr-8' : 'md:col-start-2 md:pl-8',
              ]"
              data-aos="fade-up"
              :data-aos-delay="index * 100"
            >
              <div
                :class="[
                  'p-8 rounded-2xl border border-secondary/30 backdrop-blur-sm transition-transform duration-300 hover:scale-105',
                  'bg-gradient-to-br ' + period.color,
                ]"
              >
                <!-- Your content inside -->
                <div class="flex items-center space-x-3 mb-6">
                  <div
                    class="w-12 h-12 bg-accent/20 rounded-xl flex items-center justify-center"
                  >
                    <!-- <component :is="period.icon" class="w-6 h-6 text-accent" /> -->
                    <component :is="iconsMap[period.icon]" class="w-6 h-6 text-accent" />

                  </div>
                  <h3 class="text-xl md:text-2xl font-bold">
                    {{ period.title }}
                  </h3>
                </div>
                <div
                  class="flex items-center gap-1 my-2"
                  :style="
                    period.location === 'United States'
                      ? { color: '#51a2ff' }
                      : {}
                  "
                  :class="
                    period.location === 'United States'
                      ? '[#51a2ff]'
                      : 'text-accent'
                  "
                >
                  <MapPin v-if="period.hasMapPin" class="w-4 h-4" />
                  <p class="leading-relaxed">{{ period.location }}</p>
                </div>

                <div class="space-y-4 pl-5">
                  <div
                    v-for="(highlight, hIndex) in period.highlights"
                    :key="hIndex"
                    class="flex items-start space-x-3"
                    data-aos="fade-right"
                    :data-aos-delay="index * 200"
                  >
                    <div
                      class="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                      :style="
                        period.location === 'United States'
                          ? { backgroundColor: '#51a2ff' }
                          : {}
                      "
                      :class="
                        period.location === 'United States'
                          ? '#51a2ff'
                          : 'bg-accent'
                      "
                    ></div>

                    <p class="text-muted-foreground leading-relaxed">
                      {{ highlight }}
                    </p>
                  </div>
                  <div
                    v-if="period.contribution"
                    class="max-w-4xl mx-auto p-2 md:p-4 bg-gradient-to-br from-accent/10 to-accent/5 rounded-2xl border border-accent/20"
                  >
                    <p class="text-accent">Key Contribution</p>
                    <span class="text-muted-foreground leading-relaxed">{{
                      period.contribution
                    }}</span>
                  </div>
                </div>
                <!-- Additional content sections here -->
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Publications Section -->
      <transition name="fade" appear>
        <section class="mt-24 mb-16" v-if="showPublications">
          <div class="text-center mb-12">
            <div class="mb-12">
              <h2 class="text-2xl md:text-3xl font-bold mb-4">
                Early Thought Leadership
                <span class="text-accent">(1997–2010)</span>
              </h2>
              <div
                class="w-20 h-1 bg-gradient-to-r from-accent to-accent/30 mx-auto mb-4"
              ></div>
              <div class="grid gap-6">
                <div
                  class="block p-6 mb-6 bg-gradient-to-r from-secondary/20 to-secondary/10 rounded-xl border border-secondary/30 hover:border-accent/30 transition-transform duration-300 hover:scale-105 text-left"
                >
                  <div
                    class="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
                  >
                    <div class="flex-1">
                      <div class="flex items-center space-x-3 mb-2">
                        <span class="text-accent font-bold text-lg"
                          >1997, 2000, 2003</span
                        >
                        <span class="text-muted-foreground">•</span>

                        <span class="text-accent font-medium">
                          White Paper Series: “Digital Walled Gardens”</span
                        >
                      </div>
                      <h4 class="font-medium leading-relaxed mb-2">
                        A three-part exploration of how individuals and
                        organizations would communicate and collaborate in
                        emerging online spaces—predicting the rise of secure
                        digital communities, private platforms, and
                        controlled-access communication systems.
                      </h4>
                      <small style="color: gray" class="block"></small>
                    </div>
                    <BookOpen class="w-6 h-6 text-accent flex-shrink-0" />
                  </div>
                </div>
                <div
                  class="block p-6 mb-6 bg-gradient-to-r from-secondary/20 to-secondary/10 rounded-xl border border-secondary/30 hover:border-accent/30 transition-transform duration-300 hover:scale-105 text-left"
                >
                  <div
                    class="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
                  >
                    <div class="flex-1">
                      <div class="flex items-center space-x-3 mb-2">
                        <span class="text-accent font-bold text-lg">2006</span>
                        <span class="text-muted-foreground">•</span>
                        <span class="text-accent font-medium"
                          >White Paper: “Culture of Communication</span
                        >
                      </div>
                      <h4 class="font-medium leading-relaxed mb-2">
                        A modern organizational theory examining how digital
                        technologies transform team behavior, internal
                        communication, and productivity. Widely referenced in
                        education, nonprofit, and business leadership circles.
                      </h4>
                    </div>
                    <BookOpen class="w-6 h-6 text-accent flex-shrink-0" />
                  </div>
                </div>
                <div
                  class="block p-6 mb-6 bg-gradient-to-r from-secondary/20 to-secondary/10 rounded-xl border border-secondary/30 hover:border-accent/30 transition-transform duration-300 hover:scale-105 text-left"
                >
                  <div
                    class="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
                  >
                    <div class="flex-1">
                      <div class="flex items-center space-x-3 mb-2">
                        <span class="text-accent font-bold text-lg">2010</span>
                        <span class="text-muted-foreground">•</span>
                        <span class="text-accent font-medium"
                          >Online Publications: “Culture of Communication”</span
                        >
                      </div>
                      <h4 class="font-medium leading-relaxed mb-2">
                        A series of articles and frameworks expanding the 2006
                        white paper, offering practical systems for improving
                        communication culture within software teams and
                        innovation environments.
                      </h4>
                      <small style="color: gray" class="block"></small>
                    </div>
                    <BookOpen class="w-6 h-6 text-accent flex-shrink-0" />
                  </div>
                </div>
              </div>
            </div>
            <h2 class="text-3xl md:text-4xl font-bold mb-4">
              Global Health & EMS Systems Research
              <span class="text-accent">(2020–2025) </span>
            </h2>
            <div
              class="w-20 h-1 bg-gradient-to-r from-accent to-accent/30 mx-auto"
            ></div>
          </div>
          <div class="grid gap-6">
            <transition-group name="list-fade" tag="div">
              <a
                v-for="(pub, index) in publications"
                :key="index"
                :href="pub.url"
                target="_blank"
                rel="noopener noreferrer"
                class="block p-6 mb-6 bg-gradient-to-r from-secondary/20 to-secondary/10 rounded-xl border border-secondary/30 hover:border-accent/30 transition-transform duration-300 hover:scale-105"
                v-intersect.once="() => (revealPubs[index] = true)"
              >
                <transition name="scale-fade" appear>
                  <div
                    v-if="revealPubs[index]"
                    class="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
                  >
                    <div class="flex-1">
                      <div class="flex items-center space-x-3 mb-2">
                        <span class="text-accent font-bold text-lg">{{
                          pub.year
                        }}</span>
                        <span class="text-muted-foreground">•</span>
                        <span class="text-accent font-medium">{{
                          pub.journal
                        }}</span>
                      </div>
                      <h4 class="font-medium leading-relaxed">
                        {{ pub.title }}
                      </h4>
                      <small style="color: gray"
                        >Publisher - {{ pub.publisher }}</small
                      >
                    </div>
                    <BookOpen class="w-6 h-6 text-accent flex-shrink-0" />
                  </div>
                </transition>
              </a>
            </transition-group>
          </div>
        </section>
      </transition>

      <!-- Vision Section -->
      <transition name="fade" appear>
        <section class="mt-20" v-if="showVision">
          <div class="text-center">
            <h2 class="text-2xl md:text-3xl font-bold mb-8">Ongoing Vision</h2>
            <div
              class="w-16 h-1 bg-gradient-to-r from-accent to-accent/30 mx-auto mb-12"
            ></div>

            <transition name="scale-fade" appear>
              <div
                class="max-w-4xl mx-auto p-8 md:p-12 bg-gradient-to-br from-accent/10 to-accent/5 rounded-2xl border border-accent/20 transition-transform duration-300 hover:scale-105"
                v-if="showVision"
              >
                <blockquote
                  class="leading-relaxed text-muted-foreground italic mb-6"
                >
                  "My mission is to contribute to the growth of Rwanda’s tech
                  industry into Africa’s premier hub for software development. I
                  achieve this by partnering and investing in talented
                  individuals, constructing practical systems, and mentoring the
                  next generation of founders."
                </blockquote>
                <cite class="text-accent font-medium text-lg"
                  >— Rob Rickard</cite
                >
              </div>
            </transition>

            <div
              class="w-24 h-1 bg-gradient-to-r from-accent to-accent/30 mx-auto mt-12"
            ></div>
          </div>
        </section>
      </transition>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useHead } from "@vueuse/head";
import AOS from "aos";
import "aos/dist/aos.css";
import { Motion } from "@motionone/vue";
import {
  Building,
  Globe,
  Lightbulb,
  TrendingUp,
  Users,
  BookOpen,
  Award,
  MapPin,
} from "lucide-vue-next";

useHead({
  title: "Rob Rickard CV & Timeline",
  meta: [
    {
      name: "description",
      content:
        "Rob Rickard’s professional CV, timeline, and accomplishments as a tech entrepreneur, mentor, and product strategist in Rwanda and the US.",
    },
    { property: "og:title", content: "Rob Rickard CV & Timeline" },
    {
      property: "og:description",
      content:
        "Explore the professional journey and achievements of Rob Rickard, founder of RWBuild and Guhemba.",
    },
    { property: "og:type", content: "profile" },
    { property: "og:url", content: "https://robrickard.com/cv" },
    {
      property: "og:image",
      content: "https://robrickard.com/img/robPortrait.65dd6588.png",
    },
  ],
  script: [
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Person",
        name: "Rob Rickard",
        description:
          "Tech entrepreneur, mentor, and product strategist. Founder of RWBuild and Guhemba.",
        alumniOf: {
          "@type": "Organization",
          name: "RWBuild",
        },
        worksFor: {
          "@type": "Organization",
          name: "Guhemba Ltd",
        },
        address: {
          "@type": "PostalAddress",
          addressLocality: "Kigali",
          addressCountry: "Rwanda",
        },
      }),
    },
  ],
});

const showHeader = ref(true);
const showPublications = ref(true);
const showVision = ref(true);

const timelinePeriods = ref([
  {
    years: "1994–2013",
    title: "US Technology Leadership",
    highlights: [
      "1994–2013: Introduced email and electronic document management systems to public and private institutions across the Upper Midwest as a leading technology advisor. Delivered technology services and solutions including communication servers, web development, server/data hosting, and networking for schools, NGOs, SMEs, and government agencies. Provided policy advising for K12 electronic communications, data use, and data-sharing. Developed legal frameworks and strategies for electronic archiving and organizational communication.",
      "1994–2008: Delivered keynotes, workshops, and training sessions on technology, digital culture, and organizational communication. Speaker at SLATE, ICE, ISEA ITEC, Google BrainStorm, Rosholt Leadership, Wisconsin government events, the Association of School District Administrators, the Business and Technology Summit, the Education Association Council, and the Governor of Wisconsin Technology Conference.",
      "1994–2013: Served as a consulting partner to SoftArc Inc. (later OpenText) on FirstClass, a group communication and collaboration platform.",
    ],
    icon: "Building",
    color: "from-blue-500/20 to-cyan-500/20",
    hasMapPin: true,
    location: "United States",
  },
  {
    years: "1994–2018",
    title: "CWInc Consulting and Software Development",
    highlights: [
      "1994: Started a technology consulting business during first year of college, focused on consulting, web development, communication servers, and networking — expanding into public K-12 schools, private schools, higher education, and small businesses.",
      "1997: Incorporated Common World Inc. (CWInc) in Wisconsin, offering training and organizational communication solutions, including early technology policy consulting.",
      "1999: Launched 'CW Web Wrapper,' a web templating and content management solution for schools and small businesses.",
      "2000: Expanded 'CW Web Wrapper' into a full communication and hosting platform.",
      "2001: CWInc launched FirstClass and CW Web Wrapper hosting services with a new data center.",
      "2006: Launched 'Culture of Communication,' delivering high-impact keynotes and consulting for government, nonprofit, and business clients on digital-first communication systems and productivity. Recognized as a leading voice in communication culture within innovation and startup teams.",
      "2010: Expanded into consulting for tech startups on productivity, design planning, and team systems.",
      "2011: Expanded server and communication hosting services with a private data center supporting custom enterprise platforms.",
      "2015: CWInc began transitioning from a U.S.-based consulting firm to an international enterprise focused on software development with a hyper-focus on African startups.",
      "2017: CWInc launched the Rwanda Build Program (RWBuild), a Rwandan incubator/accelerator for software startups. In its first-year pilot, RWBuild supported three teams that launched three products.",
      "2018: CWInc registered as a foreign company in Rwanda, expanding support for RWBuild with a focus on lower-income founders and scalable software solutions.",
    ],
    keyContribution:
      "Supported foundational capacity building during Rwanda's early ICT ecosystem development.",
    // icon: "Globe",
    icon: "TrendingUp",
    // color: "from-green-500/20 to-emerald-500/20",
    color: "from-purple-500/20 to-pink-500/20",
    hasMapPin: true,
    location: "United States",
  },
  {
    years: "2013-2025",
    title: "RW Mentoring",
    highlights: [
      "2013: Began mentoring African tech startups in Kigali through the government innovation hub, kLab.",
      "2014: Built and released 'Hello Kigali,' a city discovery app for Kigali, showing by example how Rwanda developers can rapidly plan, build, and launch a product. (hellokigali.rw)",
      "2015: Shifted from public speaking and communications consulting toward software startup mentoring, investing, and early-stage venture support in Rwanda.",
      "2017: Pilot program for RWBuild to focus on process and changing the culture of development in Rwanda.",
      "2018-2025: Began full time, 10 months a year in Kigali, and 2 months remote, mentoring software teams and building Rwanda products.",
    ],
    focus: "Skills development, startup mentorship, product innovation.",
    icon: "Users",
    color: "from-green-500/20 to-emerald-500/20",
    hasMapPin: true,
    location: "Rwanda",
  },
  {
    years: "2017-2025",
    title: "RWBuild Tech Hub",
    highlights: [
      "2017: Founded the Rwanda Build Program (RWBuild), a Kigali-based tech hub and startup studio. (RwandaBuildProgram.rw)",
      "2018: Expanded CWInc’s presence in Rwanda and partnered with multiple ministries on national digital agendas, technology solutions, and policy guidance.",
      "2018: Founded Guhemba Ltd. and launched the initial MVP of the Guhemba payment portal.",
      "2019–2025: RWBuild hosted major ecosystem events including multiple Code Summits, 3-Week Capacity Building Training programs, and various industry events.",
      "2021: RWBuild launched Rooftop Ikawa Café as a real-world testing environment for products and services (Ikawa.rw).",
    ],
    icon: "Lightbulb",
    color: "from-yellow-500/20 to-orange-500/20",
    hasMapPin: true,
    location: "Rwanda",
  },

  {
    years: "2014-2025",
    title: "RWBuild Product Launches",
    highlights: [
      "2014: Kigali City Guide – Hello Kigali App.",
      "2017: RWBuild Pilot – launched ifeza.rw (e-commerce platform still running to date).",
      "2018: Founder of Guhemba Ltd., Guhemba Payment Solution (MVP).",
      "2019: EventsBash launched.",
      "2020: In partnership with VCU Health, the Ministry of Health, and U.S. institutions, RWBuild designed and released a mobile data-collection app for ambulances (early version of 912Rwanda).",
      "2020: Core architect of MurugoCloud, an ecosystem platform supporting 3rd-party services and RWBuild projects (Murugo.Cloud).",
      "2021: Guhemba Payment Solution App launched (pilot at Rooftop Ikawa).",
      "2022: Guhemba Restaurant Payments launched.",
      "2023: Candidate Platform for Jobs launched (Candidate.rw).",
      "2023: Launched Phase 1 of the 912Rwanda platform – Rwanda’s national emergency ambulance dispatch and field data system (official dispatch system for MoH/RBC/SAMU).",
      "2024: Guhemba launched Invoicing & eShop services.",
      "2024: Candidate Platform expanded to support founders & startups.",
      "2025: Released AI.Murugo.Cloud – AI agents for Kigali and the startup community.",
      "2025: Launched Phase 2 of 912Rwanda – advanced rapid patient data collection and facility-destination support.",
    ],
    contribution:
      "Rob Rickard, as the core architect, initial designer, chief project manager, and team lead for each of the products in Rwanda, oversaw the projects with junior team members. Over time, the team members gradually assumed greater leadership and control over their respective projects.",
    color: "from-blue-500/20 to-indigo-500/20",
    hasMapPin: true,
    icon: "TrendingUp",
    location: "Rwanda",
  },
]);

const publications = ref([
  {
    year: "2025",
    journal: "BMJ Open",
    title:
      "Use of an innovative electronic communications platform (912Rwanda) to improve prehospital transport of injured people in Rwanda: protocol for a type 2 hybrid effectiveness-implementation interrupted time series study",
    url: "https://bmjopen.bmj.com/content/15/8/e100826.info",
    publisher: "Laura quinn",
  },
  {
    year: "2025",
    journal: "BMJ Open (NIHR)",
    title:
      "Developing and integrating a destination decision support algorithm into an innovative electronic communication platform to improve injury care service coordination in Rwanda: the Rwanda912 study protocol",
    url: "https://bmjopen.bmj.com/content/15/6/e102355",
    publisher: "Aurore Nishimwe",
  },
  {
    year: "2025",
    journal: "International Journal of Emergency Medicine",
    title:
      "Reaching the right facility for emergency patients - destinations of patients transported by emergency medical services in Kigali, Rwanda",
    url: "https://intjem.biomedcentral.com/articles/10.1186/s12245-025-00853-z",
    publisher: "Ephrem Daniel Sheferaw",
  },
  {
    year: "2024",
    journal: "BMJ Open (NIHR)",
    title:
      "Mapping the processes and information flows of a prehospital emergency care system in Rwanda: a process mapping exercise",
    url: "https://bmjopen.bmj.com/content/14/6/e085064",
    publisher: "Aurore Nishimwe ",
  },
  {
    year: "2023",
    journal: "NIH",
    title:
      "Challenges and opportunities to improve efficiency and quality of prehospital emergency care using an mHealth platform: Qualitative study in Rwanda",
    url: "https://pubmed.ncbi.nlm.nih.gov/37767314/",
    publisher: "Mediatrice Niyonsaba ",
  },
  {
    year: "2020",
    journal: "Global Health Innovation Journal",
    title:
      "Collaboration for preliminary design of a mobile health solution for ambulance dispatch in Rwanda",
    url: "https://journals.uct.ac.za/index.php/GHI/article/view/986",
    publisher: "Ashley Rosenberg",
  },
]);

const revealItems = ref(timelinePeriods.value.map(() => false));
const revealPubs = ref(publications.value.map(() => false));

const iconsMap = computed(() => ({
  Building,
  Globe,
  Lightbulb,
  TrendingUp,
  Users,
  BookOpen,
  Award,
}));
// eslint-disable-next-line no-unused-vars
function getIcon(name) {
  return iconsMap.value[name] || null;
}

onMounted(() => {
  AOS.init({
    once: true,
    duration: 1200,
  });
});

// Custom directive for v-intersect
// eslint-disable-next-line no-unused-vars
function intersect(el, binding) {
  const options = { threshold: 0.1 };
  const callback = (entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        binding.value();
        observer.unobserve(entry.target);
      }
    });
  };
  const observer = new window.IntersectionObserver(callback, options);
  observer.observe(el);
}

// Register the custom directive for v-intersect
import { getCurrentInstance } from "vue";
const instance = getCurrentInstance();
if (instance && instance.appContext && instance.appContext.app) {
  instance.appContext.app.directive("intersect", {
    mounted: intersect,
  });
}
</script>

<style scoped>
/* Transitions */

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.6s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.scale-fade-enter-active,
.scale-fade-leave-active {
  transition: all 0.3s ease;
}
.scale-fade-enter-from,
.scale-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.list-fade-enter-active,
.list-fade-leave-active {
  transition: opacity 0.3s ease;
}
.list-fade-enter-from,
.list-fade-leave-to {
  opacity: 0;
}
</style>
